import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Dropdown } from 'primereact/dropdown';

const superCleanString = (str) => {
    if (!str) return '';
    return str
        .toString()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/đ/g, 'd')
        .replace(/Đ/g, 'D')
        .replace(/\s+/g, ' ')
        .toLowerCase()
        .trim();
};

const SearchableFocusDropdown = ({ value, options, onChange, placeholder, disabled, panelUniqueClass, className, style, itemTemplate }) => {
    const dropdownRef = useRef(null);
    const focusTrapRef = useRef(null);

    const handleShow = () => {
        setTimeout(() => {
            try {
                const panel = document.querySelector(`.${panelUniqueClass}`);
                if (panel) {
                    const filterInput = panel.querySelector('.p-dropdown-filter');
                    if (filterInput && typeof filterInput.focus === 'function') {
                        filterInput.focus();

                        focusTrapRef.current = (e) => {
                            if (e.target !== filterInput) {
                                filterInput.focus();
                            }
                        };

                        panel.addEventListener('focusin', focusTrapRef.current);
                    }
                }
            } catch (err) {
                console.warn("Focus error:", err);
            }
        }, 100);
    };

    const handleHide = () => {
        const panel = document.querySelector(`.${panelUniqueClass}`);
        if (panel && focusTrapRef.current) {
            panel.removeEventListener('focusin', focusTrapRef.current);
        }
    };

    return (
        <Dropdown
            ref={dropdownRef}
            value={value}
            options={options}
            onChange={onChange}
            filter
            filterBy="label,searchKey"
            placeholder={placeholder}
            className={className || "custom-dropdown"}
            panelClassName={panelUniqueClass}
            style={style || { width: '100%' }}
            appendTo={document.body}
            onShow={handleShow}
            onHide={handleHide}
            disabled={disabled}
            itemTemplate={itemTemplate}
            emptyFilterMessage="Không tìm thấy kết quả phù hợp"
        />
    );
};

const ArticleRewardManagementForm = ({
    isOpen,
    onClose,
    onSuccess,
    formData,
    setFormData,
    users = [],
    isReadOnly = false,
    toast = null,
    allRequests = []
}) => {
    const [availableArticles, setAvailableArticles] = useState([]);
    const [selectedIds, setSelectedIds] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [selectedUserId, setSelectedUserId] = useState(null);

    const showMessage = (severity, summary, detail) => {
        if (toast && toast.current) {
            toast.current.show({ severity, summary, detail, life: 3000 });
        } else {
            alert(`${summary}: ${detail}`);
        }
    };

    const userOptions = useMemo(() => {
        return users.map(u => {
            const fullName = `${u.LastName || ''} ${u.FirstName || ''}`.trim();
            const email = u.Email || u.email || '';
            const dept = u.DepartmentName || '';
            const displayLabel = email ? `${fullName} - ${email}` : fullName;
            const searchStr = superCleanString(`${fullName} ${email} ${dept}`);

            return { label: displayLabel, value: u.Id, searchKey: searchStr, fullName: fullName, email: email, dept: dept };
        });
    }, [users]);

    const userItemTemplate = (option) => {
        return (
            <div style={{ display: 'flex', flexDirection: 'column', padding: '4px 0' }}>
                <span style={{ fontWeight: 'bold', color: '#333' }}>{option.fullName}</span>
                <span style={{ fontSize: '12px', color: '#666' }}>{option.email} {option.dept ? ` - ${option.dept}` : ''}</span>
            </div>
        );
    };

    useEffect(() => {
        const abortController = new AbortController();
        const userId = formData.UserId;

        if (userId && userId !== selectedUserId) {
            setSelectedUserId(userId);
            fetchArticlesByUserId(userId, abortController.signal);
        } else if (!userId) {
            setAvailableArticles([]);
            setSelectedIds([]);
            setSelectedUserId(null);
        }

        return () => abortController.abort();
    }, [formData.UserId, selectedUserId]);

    useEffect(() => {
        if (isOpen) {
            try {
                let ids = typeof formData.ArticleIdsJson === 'string'
                    ? JSON.parse(formData.ArticleIdsJson || '[]')
                    : (formData.ArticleIds || []);
                setSelectedIds(ids.map(id => Number(id)));
            } catch (e) {
                setSelectedIds([]);
            }

            if (formData.UserId) {
                setSelectedUserId(formData.UserId);
                fetchArticlesByUserId(formData.UserId);
            }
        } else {
            setAvailableArticles([]);
            setSelectedIds([]);
            setSelectedUserId(null);
        }
    }, [isOpen, formData]);

    const fetchArticlesByUserId = async (userId, signal) => {
        if (!userId) return;
        setIsLoading(true);
        const token = localStorage.getItem('accessToken');
        if (!token) {
            showMessage('error', 'Lỗi xác thực', 'Không tìm thấy token. Vui lòng đăng nhập lại.');
            setIsLoading(false);
            return;
        }

        try {
            const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/scientificarticle?userId=${userId}`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                },
                signal
            });

            if (res.ok) {
                const data = await res.json();
                const actualData = Array.isArray(data) ? data : (data.data || []);

                const usedArticleIds = [];
                allRequests.forEach(req => {
                    if (Number(req.UserId) === Number(userId) && req.Id !== formData.Id) {
                        try {
                            const ids = typeof req.ArticleIdsJson === 'string'
                                ? JSON.parse(req.ArticleIdsJson || '[]')
                                : (req.ArticleIds || []);
                            usedArticleIds.push(...ids.map(id => Number(id)));
                        } catch (e) {
                            console.error("Lỗi parse ArticleIdsJson:", e);
                        }
                    }
                });

                const filteredArticles = actualData.filter(a => !usedArticleIds.includes(Number(a.Id || a.id)));
                setAvailableArticles(filteredArticles);

            } else if (res.status === 401) {
                showMessage('error', 'Phiên đăng nhập hết hạn', 'Vui lòng đăng nhập lại.');
                setAvailableArticles([]);
            } else {
                console.error('Lỗi tải bài báo:', res.status);
                showMessage('error', 'Lỗi', 'Không thể tải danh sách bài báo của giảng viên này.');
                setAvailableArticles([]);
            }
        } catch (error) {
            if (error.name !== 'AbortError') {
                console.error('Lỗi kết nối:', error);
                showMessage('error', 'Lỗi kết nối', 'Không thể kết nối đến máy chủ.');
                setAvailableArticles([]);
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleCheckboxChange = (articleId) => {
        if (isReadOnly) return;
        const numId = Number(articleId);
        if (selectedIds.includes(numId)) {
            setSelectedIds(selectedIds.filter(id => id !== numId));
        } else {
            setSelectedIds([...selectedIds, numId]);
        }
    };

    const handleInputChange = (e) => {
        if (isReadOnly) return;
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleUserChange = (value) => {
        if (isReadOnly) return;
        setFormData({ ...formData, UserId: value });
        setAvailableArticles([]);
        setSelectedIds([]);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.UserId || parseInt(formData.UserId) === 0) {
            showMessage('warn', 'Thiếu thông tin', 'Vui lòng chọn Người đề xuất (Giảng viên)!');
            return;
        }

        if (selectedIds.length === 0) {
            showMessage('warn', 'Chưa chọn bài báo', 'Vui lòng chọn ít nhất một bài báo!');
            return;
        }

        const selectedData = availableArticles.filter(a => selectedIds.includes(Number(a.Id || a.id)));
        const totalAmountValue = selectedData.reduce((sum, item) => {
            const reward = Number(item.RewardAmount || item.rewardAmount || 0);
            return sum + reward;
        }, 0);

        const payload = {
            ...formData,
            UserId: parseInt(formData.UserId),
            ArticleIds: selectedIds,
            ArticleCount: selectedIds.length,
            TotalAmount: totalAmountValue,
            Note: formData.Note || ''
        };

        const token = localStorage.getItem('accessToken');
        if (!token) {
            showMessage('error', 'Lỗi xác thực', 'Không tìm thấy token. Vui lòng đăng nhập lại.');
            return;
        }

        setIsSubmitting(true);
        try {
            const method = payload.Id ? 'PUT' : 'POST';
            const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
            const currentUserId = currentUser.Id || '';

            const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/articlereward?userId=${currentUserId}`, {
                method: method,
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload)
            });

            if (res.ok) {
                showMessage('success', 'Thành công', payload.Id ? 'Cập nhật thành công!' : 'Thêm mới thành công!');
                onSuccess();
            } else {
                let errorMsg = 'Lưu dữ liệu thất bại!';
                try {
                    const errData = await res.json();
                    errorMsg = errData.message || errData.error || errorMsg;
                } catch (err) { }
                showMessage('error', 'Lỗi từ máy chủ', errorMsg);
            }
        } catch (error) {
            console.error('Lỗi kết nối:', error);
            showMessage('error', 'Lỗi kết nối', 'Không thể kết nối đến máy chủ. Vui lòng thử lại.');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="modal-overlay" style={{ zIndex: 1000 }}>
            <div className="modal-box" style={{ width: '90%', maxWidth: '900px', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
                <div className="modal-header" style={{ backgroundColor: '#003399', color: '#fff', padding: '15px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <h3 style={{ margin: 0, fontSize: '18px', color: '#fff', paddingRight: '20px', lineHeight: '1.4' }}>
                        {isReadOnly ? 'Chi tiết xét thưởng công bố' : (formData.Id ? 'Chỉnh sửa xét thưởng' : 'Thêm mới xét thưởng')}
                    </h3>
                    <button className="close-btn" onClick={onClose} style={{ color: '#fff', background: 'none', border: 'none', fontSize: '26px', cursor: 'pointer', lineHeight: '1', flexShrink: 0, marginTop: '-2px' }}>&times;</button>
                </div>

                <div className="modal-body" style={{ padding: '20px', overflowY: 'auto' }}>
                    <form id="rewardAdminForm" onSubmit={handleSubmit}>
                        <div className="form-group" style={{ marginBottom: '20px' }}>
                            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#333' }}>
                                Người đề xuất (Giảng viên) <span style={{ color: 'red' }}>*</span>
                            </label>
                            <SearchableFocusDropdown
                                value={formData.UserId || ''}
                                options={userOptions}
                                onChange={(e) => handleUserChange(e.value)}
                                placeholder="Chọn Giảng viên"
                                className="custom-dropdown"
                                style={{ width: '100%' }}
                                panelUniqueClass="custom-dropdown-panel-reward-proposer-user"
                                disabled={isReadOnly || formData.Id !== 0}
                                itemTemplate={userItemTemplate}
                            />
                        </div>

                        <div style={{ marginBottom: '15px' }}>
                            <h4 style={{ fontSize: '15px', color: '#333', fontWeight: 'bold', marginBottom: '10px' }}>
                                Chọn bài báo trong danh sách
                            </h4>
                            {!formData.UserId && !isReadOnly && (
                                <div style={{ padding: '10px', backgroundColor: '#fff3cd', borderLeft: '4px solid #ffc107', marginBottom: '15px', borderRadius: '4px' }}>
                                    <i className="fa-solid fa-info-circle" style={{ marginRight: '8px', color: '#856404' }}></i>
                                    Vui lòng chọn giảng viên để hiển thị danh sách bài báo
                                </div>
                            )}
                        </div>

                        <div style={{ overflowX: 'auto', border: '1px solid #ddd', borderRadius: '4px', marginBottom: '25px', maxHeight: '350px', overflowY: 'auto' }}>
                            <table className="custom-table" style={{ minWidth: '800px', margin: 0, border: 'none', fontSize: '13px' }}>
                                <thead style={{ backgroundColor: '#f9f9f9', position: 'sticky', top: 0, zIndex: 1, borderBottom: '2px solid #ddd' }}>
                                    <tr>
                                        <th width="5%" style={{ textAlign: 'center', padding: '12px' }}>Chọn</th>
                                        <th width="35%" style={{ color: '#003399', padding: '12px' }}>Bài báo</th>
                                        <th width="15%" style={{ color: '#003399', textAlign: 'center', padding: '12px' }}>Ngày công bố</th>
                                        <th width="15%" style={{ color: '#003399', padding: '12px' }}>Tạp chí</th>
                                        <th width="10%" style={{ color: '#003399', padding: '12px' }}>Loại tạp chí</th>
                                        <th width="10%" style={{ color: '#003399', textAlign: 'right', padding: '12px' }}>Mức thưởng</th>
                                        <th width="10%" style={{ color: '#003399', textAlign: 'right', padding: '12px' }}>Mức thưởng chính</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {!formData.UserId && !isReadOnly ? (
                                        <tr><td colSpan="7" style={{ textAlign: 'center', padding: '30px', color: '#888' }}>Chưa chọn giảng viên</td></tr>
                                    ) : isLoading ? (
                                        <tr><td colSpan="7" style={{ textAlign: 'center', padding: '30px' }}><i className="fa-solid fa-spinner fa-spin"></i> Đang tải dữ liệu</td></tr>
                                    ) : availableArticles.length === 0 ? (
                                        <tr><td colSpan="7" style={{ textAlign: 'center', padding: '30px', color: '#999' }}>Giảng viên này chưa có bài báo nào hoặc tất cả đã được xét thưởng</td></tr>
                                    ) : (
                                        availableArticles.map((article) => {
                                            let displayDate = 'N/A';
                                            if (article.PublicationDate) {
                                                try {
                                                    const date = new Date(article.PublicationDate);
                                                    if (!isNaN(date.getTime())) {
                                                        displayDate = date.toLocaleDateString('vi-VN');
                                                    } else if (typeof article.PublicationDate === 'string' && article.PublicationDate.includes('/')) {
                                                        displayDate = article.PublicationDate;
                                                    }
                                                } catch (e) {
                                                    displayDate = article.PublicationDate;
                                                }
                                            }
                                            return (
                                                <tr key={article.Id} style={{ borderBottom: '1px solid #eee' }}>
                                                    <td style={{ textAlign: 'center', padding: '10px' }}>
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedIds.includes(Number(article.Id || article.id))}
                                                            onChange={() => handleCheckboxChange(article.Id || article.id)}
                                                            disabled={isReadOnly}
                                                            style={{ cursor: isReadOnly ? 'not-allowed' : 'pointer', width: '16px', height: '16px' }}
                                                        />
                                                    </td>
                                                    <td style={{ fontWeight: '500', padding: '10px', color: '#333' }}>
                                                        {article.Title || article.ArticleName || 'N/A'}
                                                    </td>
                                                    <td style={{ textAlign: 'center', padding: '10px', color: '#555' }}>{displayDate}</td>
                                                    <td style={{ padding: '10px', color: '#555' }}>{article.JournalName || article.Journal || 'N/A'}</td>
                                                    <td style={{ padding: '10px', color: '#555' }}>{article.JournalType || 'Khác'}</td>
                                                    <td style={{ textAlign: 'right', padding: '10px', color: '#27ae60', fontWeight: 'bold' }}>
                                                        {Number(article.RewardAmount || article.rewardAmount || 0).toLocaleString()}
                                                    </td>
                                                    <td style={{ textAlign: 'right', padding: '10px', color: '#27ae60', fontWeight: 'bold' }}>
                                                        {Number(article.MainAuthorReward || 0).toLocaleString()}
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    )}
                                </tbody>
                            </table>
                        </div>

                        <div className="form-group" style={{ marginBottom: '10px' }}>
                            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#333' }}>Ghi chú chung</label>
                            <textarea
                                name="Note"
                                className="form-input"
                                rows="3"
                                value={formData.Note || ''}
                                onChange={handleInputChange}
                                disabled={isReadOnly}
                                placeholder="Ghi chú"
                                style={{ width: '100%', padding: '10px', border: '1px solid #ccc', borderRadius: '4px', outline: 'none', backgroundColor: isReadOnly ? '#f5f5f5' : '#fff' }}
                            />
                        </div>
                    </form>
                </div>

                <div className="modal-footer" style={{ borderTop: '1px solid #eee', padding: '15px 20px', display: 'flex', flexWrap: 'wrap', justifyContent: 'flex-end', gap: '10px', backgroundColor: '#f9f9f9' }}>
                    <button type="button" className="btn-cancel" onClick={onClose} style={{ padding: '8px 20px', backgroundColor: '#888', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
                        {isReadOnly ? 'Đóng' : 'Hủy'}
                    </button>
                    {!isReadOnly && (
                        <button
                            type="submit"
                            form="rewardAdminForm"
                            className="btn-submit"
                            disabled={isSubmitting}
                            style={{
                                padding: '8px 20px',
                                backgroundColor: '#0056b3',
                                color: '#fff',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: isSubmitting ? 'not-allowed' : 'pointer',
                                opacity: isSubmitting ? 0.7 : 1
                            }}
                        >
                            {isSubmitting ? 'Đang xử lý...' : 'Hoàn tất'}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ArticleRewardManagementForm;